/**
 * Idle logout — ends the session after IDLE_TIMEOUT_MS without user input.
 *
 * Product rule: max 10 minutes idle, then log out. This also kills the
 * stale-state problem — a player who walks away for hours can never come back
 * to a long-dead screen full of old numbers; they re-login and every screen
 * refetches fresh on mount.
 *
 * A hidden tab's timers are throttled by the browser, so the interval alone
 * can't be trusted — the visibilitychange handler re-checks the moment the
 * player returns, before they can interact with anything stale.
 */
export const IDLE_TIMEOUT_MS = 10 * 60 * 1000;

const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'] as const;
const CHECK_EVERY_MS = 30_000;

/** Start watching; returns a teardown. `onExpire` fires at most once. */
export function installIdleLogout(onExpire: () => void, timeoutMs = IDLE_TIMEOUT_MS): () => void {
  let lastActivity = Date.now();
  let expired = false;

  const bump = () => { lastActivity = Date.now(); };
  const check = () => {
    if (expired || Date.now() - lastActivity < timeoutMs) return;
    expired = true;
    teardown();
    onExpire();
  };
  const onVisible = () => { if (!document.hidden) check(); };

  ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, bump, { passive: true }));
  document.addEventListener('visibilitychange', onVisible);
  const timer = window.setInterval(check, CHECK_EVERY_MS);

  function teardown() {
    ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, bump));
    document.removeEventListener('visibilitychange', onVisible);
    window.clearInterval(timer);
  }
  return teardown;
}
